import { Fragment, useCallback, useMemo } from "react";
import { NavLink, useLocation } from "react-router";
import { chatbots } from "@/data/chatbots.js";
import { graphics } from "@/data/graphics.js";
import { information } from "@/data/information.js";
import { overlays } from "@/data/overlays.jsx";
import "./global-sidebar-menu.css";
import { dashboards } from "@/data/dashboards.jsx";

const appEnv = import.meta.env.VITE_APP_ENV || "dev";

const overrideShow = appEnv === "dev" || appEnv === "qa";

interface GlobalSidebarMenuProps {
  searchValue?: string;
  onLinkClick?: () => void;
}

/** Represents a minimal item shape from any data source */
interface SidebarItem {
  name: string;
  link: string;
  show: boolean | string;
  category?: string;
  isNew?: boolean;
  isUpdated?: boolean;
  isFeatured?: boolean;
  order?: number;
}

interface MenuItem {
  name: string;
  link: string;
  updated?: boolean;
  new?: boolean;
  featured?: boolean;
  show?: boolean | string;
  className?: string;
}

interface MenuSection {
  name: string;
  children: MenuItem[];
}

type MainMenuItem = MenuItem | MenuSection | null;

export default function GlobalSidebarMenu({
  searchValue = "",
  onLinkClick = () => {},
}: GlobalSidebarMenuProps) {
  const location = useLocation();
  const currentPage = useMemo(() => location.pathname, [location]);

  const sortItems = useCallback(
    (a: { name: string; order?: number }, b: { name: string; order?: number }) => {
      if (Object.hasOwn(a, "order") && Object.hasOwn(b, "order")) {
        return (a.order ?? 0) - (b.order ?? 0);
      }
      return a.name.localeCompare(b.name);
    },
    [],
  );

  const allOverlays = useMemo(() => {
    return Object.values(overlays)
      .filter((overlay) => overlay.category !== "progress")
      .filter((overlay) => overlay.show || overrideShow)
      .filter((overlay) => {
        if (searchValue.length > 0) {
          return overlay.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const progressBars = useMemo(() => {
    return Object.values(overlays)
      .filter((overlay) => overlay.category === "progress")
      .filter((overlay) => overlay.show || overrideShow)
      .filter((overlay) => {
        if (searchValue.length > 0) {
          return overlay.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const dashboardItems = useMemo(() => {
    return Object.values(dashboards)
      .filter((dashboard) => dashboard.show || overrideShow)
      .filter((dashboard) => {
        if (searchValue.length > 0) {
          return dashboard.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const graphicsBundles = useMemo(() => {
    return (Object.values(graphics) as SidebarItem[])
      .filter((bundle) => bundle.show || overrideShow)
      .filter((bundle) => {
        if (searchValue.length > 0) {
          return bundle.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const informationItems = useMemo(() => {
    return (Object.values(information) as SidebarItem[])
      .filter((item) => item.show || overrideShow)
      .filter((item) => {
        if (searchValue.length > 0) {
          return item.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const chatbotItems = useMemo(() => {
    return (Object.values(chatbots) as SidebarItem[])
      .filter((chatbot) => chatbot.show || overrideShow)
      .filter((chatbot) => {
        if (searchValue.length > 0) {
          return chatbot.name.toLowerCase().includes(searchValue);
        }
        return true;
      })
      .sort(sortItems);
  }, [searchValue, sortItems]);

  const overlaySections = useMemo(() => {
    return [
      {
        name: "Progress Bars",
        children: progressBars.map((progressBar) => {
          return {
            name: progressBar.name.replace("Progress Bar", ""),
            link: progressBar.link,
            updated: progressBar.isUpdated ?? false,
            new: progressBar.isNew ?? false,
            show: progressBar.show,
          };
        }),
      },
      {
        name: "Fundraising Tools",
        children: allOverlays.map((overlay) => {
          return {
            name: overlay.name,
            link: overlay.link,
            updated: overlay.isUpdated ?? false,
            new: overlay.isNew ?? false,
            show: overlay.show,
          };
        }),
      },
    ];
  }, [progressBars, allOverlays]);

  const dashboardSection = useMemo(
    () =>
      dashboardItems.map((dashboard) => {
        return {
          name: dashboard.name,
          link: dashboard.link,
          updated: dashboard.isUpdated ?? false,
          new: dashboard.isNew ?? false,
          featured: dashboard.isFeatured ?? false,
          show: dashboard.show,
        };
      }),
    [dashboardItems],
  );

  const graphicsBundleSections = useMemo(
    () =>
      graphicsBundles.map((bundle) => {
        return {
          name: bundle.name,
          link: bundle.link,
          updated: bundle.isUpdated ?? false,
          new: bundle.isNew ?? false,
          featured: bundle.isFeatured ?? false,
          show: bundle.show,
        };
      }),
    [graphicsBundles],
  );

  const informationSection = useMemo(
    () =>
      informationItems.map((item) => {
        return {
          name: item.name,
          link: item.link,
          updated: item.isUpdated ?? false,
          new: item.isNew ?? false,
          featured: item.isFeatured ?? false,
          show: item.show,
        };
      }),
    [informationItems],
  );

  const chatbotSection = useMemo(
    () =>
      chatbotItems.map((item) => {
        return {
          name: item.name.replace(" Chatbot", ""),
          link: item.link,
          updated: item.isUpdated ?? false,
          new: item.isNew ?? false,
          featured: item.isFeatured ?? false,
          show: item.show,
        };
      }),
    [chatbotItems],
  );

  const formatName = useCallback((name: string): string => {
    if (name === "Leaderboard Overlay") {
      return "Leaderboard";
    }

    return name;
  }, []);

  const makeMenuItem = useCallback(
    (item: MenuItem) => {
      const innerItem = (
        <>
          {formatName(item.name)}
          {item.new && (
            <span className="sj-light-blue border-none text-white badge badge-xs w-fit font-bold">
              <span className="inline-block">NEW</span>
            </span>
          )}
          {item.updated && (
            <span className="sj-dark-yellow border-none text-black badge badge-xs w-fit font-bold">
              <span className="inline-block ">UPDATED</span>
            </span>
          )}
          {item.featured && (
            <span className="sj-primary border-none badge badge-xs w-fit font-bold">
              <span className="inline-block">FEATURED</span>
            </span>
          )}
        </>
      );

      return (
        <Fragment key={item.link}>
          <li className="hidden py-1 relative lg:flex! h-9">
            <NavLink
              to={item.link}
              className={
                item.className
                  ? `flex flex-row items-center justify-between leading-snug w-full ${item.className} ${currentPage === item.link && "menu-active"}`
                  : `flex flex-row items-center justify-between leading-snug w-full ${!item.show && overrideShow && "text-red-600"} ${currentPage === item.link && "menu-active"}`
              }
            >
              {innerItem}
            </NavLink>
          </li>
          <li className="py-1 relative flex lg:hidden h-9">
            <NavLink
              to={item.link}
              onClick={() => onLinkClick()}
              className={
                item.className
                  ? `flex flex-row items-center justify-between leading-snug w-full ${item.className} ${currentPage === item.link && "menu-active"}`
                  : `flex flex-row items-center justify-between leading-snug w-full ${!item.show && overrideShow && "text-red-600"} ${currentPage === item.link && "menu-active"}`
              }
            >
              {innerItem}
            </NavLink>
          </li>
        </Fragment>
      );
    },
    [formatName, onLinkClick, currentPage],
  );

  const makeMenuSection = useCallback(
    (section: MenuSection) => {
      return (
        <li key={section.name}>
          <h2 className="menu-title">{section.name}</h2>
          <ul>{section.children.map((child: MenuItem) => makeMenuItem(child))}</ul>
        </li>
      );
    },
    [makeMenuItem],
  );

  const makeMenuItems = useCallback(
    (item: MainMenuItem) => {
      if (!item) {
        return null;
      }
      if (Object.hasOwn(item, "children")) {
        return makeMenuSection(item as MenuSection);
      }
      return makeMenuItem(item as MenuItem);
    },
    [makeMenuItem, makeMenuSection],
  );

  const mainSection = useMemo(() => {
    const otherSections = [
      ...overlaySections,
      {
        name: "Dashboards",
        children: dashboardSection,
      },
      {
        name: "Chatbots",
        children: chatbotSection,
      },
      {
        name: "Graphics and Assets",
        children: graphicsBundleSections,
      },
      {
        name: "Information",
        children: informationSection,
      },
    ].filter((item) => item.children.length > 0);

    const nowMonth = new Date().getMonth();

    return [
      {
        name: "Home",
        link: "/",
        className: "font-bold text-lg",
      },
      nowMonth === 9
        ? {
            name: "CCAM Tools",
            link: "/ccam",
            className: " py-1 font-bold",
          }
        : null,
      ...otherSections,
    ].filter(Boolean) as MainMenuItem[];
  }, [
    overlaySections,
    chatbotSection,
    graphicsBundleSections,
    informationSection,
    dashboardSection,
  ]);

  return (
    <ul className="global-sidebar-menu menu text-base-content w-full md:w-80 pb-10">
      {mainSection.map((section) => {
        return makeMenuItems(section);
      })}
    </ul>
  );
}
